import {
  Alert,
  Badge,
  Button,
  Field,
  Modal,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Percent, Plus } from 'lucide-react';
import { type JSX, useState } from 'react';

import { ApiError } from '../../api/client';
import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import {
  createVatRate,
  formatVatRate,
  listVatRates,
  ARCA_SUPPORTED_RATES,
  updateVatRate,
  type UpdateVatRateInput,
  type VatRate,
} from '../../invoices/vat-rates-api';

type ModalState = { mode: 'create' } | { mode: 'edit'; rate: VatRate } | null;

/** Codes from `BillingExceptionFilter` (`apps/api` `billing-exception.filter.ts`). */
function mapVatRateError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 422 && code === 'VAT_RATE_NOT_SUPPORTED') {
    return 'ARCA no admite esa alícuota. Elegí una de la lista.';
  }

  if (error.status === 409 && code === 'VAT_RATE_DUPLICATE') {
    return 'Ya existe una alícuota con ese valor.';
  }

  if (error.status === 409 && code === 'VAT_RATE_NAME_DUPLICATE') {
    return 'Ya existe una alícuota con ese nombre.';
  }

  if (error.status === 409 && code === 'VAT_RATE_DEFAULT_PROTECTED') {
    return 'La alícuota por defecto no se puede desactivar. Marcá otra como por defecto primero.';
  }

  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 404) {
    return 'La alícuota ya no existe. Actualizá la página.';
  }

  if (error.status === 422) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}

/**
 * `vat-rates/spec.md` — "Pantalla de configuración de alícuotas de IVA".
 * The hub card is gated on `fiscal.config`; the page double-checks it because
 * a direct URL must not render the table.
 */
export function VatRateConfigPage(): JSX.Element {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [modal, setModal] = useState<ModalState>(null);
  const [name, setName] = useState('');
  const [rate, setRate] = useState('');

  const canConfigure = hasPermission(currentUser.data, 'fiscal.config');
  const query = useQuery({
    queryKey: ['vat-rates'],
    queryFn: listVatRates,
    enabled: canConfigure,
  });

  async function refresh(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['vat-rates'] }),
      queryClient.invalidateQueries({ queryKey: ['vat-rate-options'] }),
    ]);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (modal === null) {
        return;
      }

      if (modal.mode === 'create') {
        await createVatRate({ name: name.trim(), rate: Number(rate) });
      } else {
        await updateVatRate(modal.rate.id, { name: name.trim() });
      }
    },
    onSuccess: async () => {
      toast('success', 'Guardado');
      setModal(null);
      await refresh();
    },
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateVatRateInput }) =>
      updateVatRate(id, input),
    onSuccess: async () => {
      toast('success', 'Guardado');
      await refresh();
    },
  });

  function openModal(state: ModalState): void {
    saveMutation.reset();
    setName(state?.mode === 'edit' ? state.rate.name : '');
    setRate('');
    setModal(state);
  }

  if (currentUser.isPending) {
    return <Skeleton className="h-40 w-full" />;
  }

  if (!canConfigure) {
    return (
      <Alert tone="danger" title="Sección restringida">
        No tenés permiso para acceder a las alícuotas de IVA.
      </Alert>
    );
  }

  const rates = query.data ?? [];
  const canSave = name.trim() !== '' && (modal?.mode === 'edit' || rate !== '');

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Percent}
        title="Alícuotas de IVA"
        description="Definí las alícuotas que se ofrecen al cargar precios y cuál es la que se propone por defecto."
        actions={
          <Button icon={Plus} onClick={() => openModal({ mode: 'create' })}>
            Nueva alícuota
          </Button>
        }
      />

      {query.isError ? (
        <Alert tone="danger" title="No pudimos cargar las alícuotas de IVA">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {patchMutation.isError ? (
        <Alert tone="danger">{mapVatRateError(patchMutation.error)}</Alert>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined ? (
        <Table>
          <caption className="sr-only">Alícuotas de IVA</caption>
          <thead>
            <tr>
              <TableHeader>Nombre</TableHeader>
              <TableHeader>Alícuota</TableHeader>
              <TableHeader>Conceptos que la usan</TableHeader>
              <TableHeader>Estado</TableHeader>
              <TableHeader>Acciones</TableHeader>
            </tr>
          </thead>
          <TableBody>
            {rates.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <span className="flex items-center gap-2">
                    {item.name}
                    {item.isDefault ? <Badge variant="info">Por defecto</Badge> : null}
                  </span>
                </TableCell>
                <TableCell>{formatVatRate(item.rate)}</TableCell>
                <TableCell>{item.billingConceptUsageCount}</TableCell>
                <TableCell>
                  <Badge variant={item.isActive ? 'success' : 'neutral'}>
                    {item.isActive ? 'Activa' : 'Inactiva'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={Pencil}
                      aria-label={`Editar ${item.name}`}
                      onClick={() => openModal({ mode: 'edit', rate: item })}
                    >
                      Editar
                    </Button>
                    {item.isDefault ? null : (
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={
                          patchMutation.isPending && patchMutation.variables?.id === item.id
                        }
                        onClick={() =>
                          patchMutation.mutate({ id: item.id, input: { isActive: !item.isActive } })
                        }
                      >
                        {item.isActive ? 'Desactivar' : 'Activar'}
                      </Button>
                    )}
                    {item.isDefault || !item.isActive ? null : (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          patchMutation.mutate({ id: item.id, input: { isDefault: true } })
                        }
                      >
                        Marcar por defecto
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      {modal !== null ? (
        <Modal
          open
          onClose={() => setModal(null)}
          title={modal.mode === 'create' ? 'Nueva alícuota' : 'Editar alícuota'}
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {saveMutation.isError ? (
              <Alert tone="danger">{mapVatRateError(saveMutation.error)}</Alert>
            ) : null}

            <Field
              label="Nombre"
              name="name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />

            {modal.mode === 'create' ? (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="vat-rate-value" className="text-sm font-medium text-slate-900">
                  Alícuota
                  <span aria-hidden="true" className="ml-0.5 text-red-600">
                    *
                  </span>
                </label>
                <select
                  id="vat-rate-value"
                  value={rate}
                  onChange={(event) => setRate(event.target.value)}
                  className="h-11 rounded-md border border-slate-300 px-3 text-sm"
                >
                  <option value="">Seleccioná una alícuota</option>
                  {ARCA_SUPPORTED_RATES.map((supported) => (
                    <option key={supported} value={supported}>
                      {formatVatRate(supported)}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-slate-900">Alícuota</span>
                <span className="text-sm text-slate-700">{formatVatRate(modal.rate.rate)}</span>
                <span className="text-xs text-slate-500">
                  La alícuota no se puede cambiar; creá una nueva si necesitás otro valor.
                </span>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button
                loading={saveMutation.isPending}
                disabled={!canSave}
                onClick={() => saveMutation.mutate()}
              >
                Confirmar
              </Button>
              <Button variant="secondary" onClick={() => setModal(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
